<template>
    <div class="messages-container px-7" ref="messagesContainer" @scroll="handleScroll">
        <template v-if="messages.length > 0">
            <div class="text-center p-4" v-if="loading">
                <nord-spinner size="l"></nord-spinner>
            </div>

            <div :class="message.is_from_user ? 'right-chat-bubble' : 'left-chat-bubble'" v-for="message in messages" :key="message.id">
                <div class="chat-bubble" :class="{'message-deleted': message.is_trashed}">
                    <!-- Text section -->
                    <template v-if="!message.is_trashed">
                        <p v-if="message.text" v-sanitize-html="message.text"></p>
                    </template>
                    <p class="text-muted" v-else-if="message.is_from_user">{{ trans('messages.message_deleted_by_you') }}</p>
                    <p class="text-muted" v-else>{{ trans('messages.message_deleted') }}</p>

                    <!-- Attachments section -->
                    <div class="attachment" v-if="!message.is_trashed && (message.has_file || message.file || message.invoice || message.creditnote || message.journal_file || message.journal)" >

                        <div v-if="message.has_file">
                            <div v-for="file in message.files" :key="file.uuid">
                                <a class="delete-link" v-on:click="deleteAttachmentSwal(message, file)" v-if="message.is_from_user && can_edit_client">
                                    <i class="fa fa-trash" v-tooltip :title="trans('online_booking.delete_attachment')"></i>
                                </a>
                                <a @click="viewMessageAttachment(file)">{{ file.file_name }}</a>
                            </div>
                        </div>

                        <div v-else>
                            <a class="delete-link" v-on:click="deleteAttachmentSwal(message, null)" v-if="message.is_from_user && can_edit_client">
                                <i class="fa fa-trash" v-tooltip :title="trans('online_booking.delete_attachment')"></i>
                            </a>
                            <a :href="getInvoiceUrl(message.invoice)" v-if="message.invoice && can_see_invoices" target="_blank">
                                {{ trans('global.invoice' ) }} {{ message.invoice.number }}
                            </a>
                            <a v-else-if="message.invoice && !can_see_invoices">
                                {{ trans('global.invoice' ) }} {{ message.invoice.number }}
                            </a>

                            <a :href="getCreditnoteUrl(message.creditnote)" v-if="message.creditnote && can_see_invoices" target="_blank">
                                {{ trans('global.creditnote' ) }} {{ message.creditnote.number }}
                            </a>
                            <a v-else-if="message.creditnote && !can_see_invoices">
                                {{ trans('global.creditnote' ) }} {{ message.creditnote.number }}
                            </a>
                        </div>

                    </div>

                </div>
                <div :class="message.is_from_user ? 'right-bubble-info' : 'left-bubble-info'" :key="message.id">
                    <a href v-on:click.prevent="deleteMessageSwal(message)">
                        <i class="zmdi zmdi-delete" v-tooltip :title="trans('messages.delete_message')" data-placement='left' v-if="message.is_from_user && !message.is_trashed"></i>
                    </a>
                    <span class="text-muted">{{ message.sent }}</span>
                    <i class="zmdi zmdi-check seen" v-if="message.is_from_user && message.is_readed"></i>

                    <span v-if="!message.is_trashed && !message.is_marked_as_unread && !message.is_from_user && can_edit_client"  class="mark-as-unread"> &middot;
                        <a href=""  v-on:click.prevent="markMessageAsUnread(message, $event)">{{ trans('messaging.mark_as_unread') }}</a>
                    </span>
                </div>
            </div>
        </template>

        <div v-if="messages.length === 0 && !loading" class="messages-placeholder">
            <div class="text-center m-t-40">
                <i class="zmdi zmdi-shield-check text-muted" style="font-size: 100px;"></i>
            </div>
            <h4 class="text-center text-muted" style="margin: 20px 30%;" v-sanitize-html="placeholder_text"></h4>
        </div>

        <div v-if="messages.length === 0 && loading" class="text-center p-4">
            <nord-spinner size="l"></nord-spinner>
        </div>
    </div>
</template>

<script>
import { http } from '../../http';

export default {
    name: 'SecureMessageList',

    props: {
        client: {
            required: true,
            type: Object,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            loading: false,
            messages: [],
            hasMoreMessages: false,
            currentPage: 1,
            scrollThreshold: 150, // pixels from the top of the scrollable area
            debounceTime: 150, // time in milliseconds
            isScrolling: false,
        };
    },

    mounted() {
        this.$event_bus.$on('load-secure-messages', this.loadSecureMessagesListener);
        this.$event_bus.$on('messageSent', this.newMessageSent);
    },

    beforeUnmount() {
        this.$event_bus.$off('load-secure-messages', this.loadSecureMessagesListener);
        this.$event_bus.$off('messageSent', this.newMessageSent);
    },

    methods: {
        newMessageSent(newMessage) {
            if (newMessage.client.id === this.client.id) {
                this.messages.push(newMessage);
                this.$nextTick(() => this.scrollBottom())
            }
        },
        handleScroll() {
            if (this.isScrolling) return;

            this.isScrolling = true;
            setTimeout(() => {
                this.isScrolling = false;

                const container = this.$refs.messagesContainer;

                if (!container) {
                    return
                }

                // Check if we're near the top of the container
                if (container.scrollTop <= this.scrollThreshold && this.hasMoreMessages && !this.loading) {
                    this.loadMore();
                }
            }, this.debounceTime);
        },
        resetPagination() {
            this.currentPage = 1;
            this.hasMoreMessages = false;
        },
        saveScrollPosition() {
            const container = document.querySelector('.messages-container');
            if (container) {
                // Save both the scroll height and position
                return {
                    scrollHeight: container.scrollHeight,
                    scrollTop: container.scrollTop
                };
            }
            return null;
        },

        restoreScrollPosition(previousPosition) {
            if (!previousPosition) return;

            const container = document.querySelector('.messages-container');
            if (container) {
                // Calculate how much the height has changed
                const heightDifference = container.scrollHeight - previousPosition.scrollHeight;

                // Maintain the relative scroll position by adding the difference
                container.scrollTop = previousPosition.scrollTop + heightDifference;
            }
        },

        loadMore() {
            if (this.hasMoreMessages && !this.loading) {
                this.currentPage++;
                this.getMessages(true);
            }
        },

        resetAttachment(messageId, attachemntId) {
            const message = this.messages.find((message => message.id === messageId))

            if (!message) {
                return;
            }

            const file = message.files[attachemntId] ?? null

            if (file) {
                delete message.files[attachemntId];
            }

            // Delete the message from the array if empty
            if (Object.values(message.files).length === 0 && message.text === '') {
                message.is_trashed = true;
            }
        },

        getMessages(loadMore = false) {
            this.loading = true;

            // Only save the scroll position if we're loading more messages
            const scrollPosition = loadMore ? this.saveScrollPosition() : null;

            http.get(`/app/client/${this.client.id}/messaging`, {
                params: {
                    page: this.currentPage,
                }
            })
                .then(({ data }) => {
                    // Reverse the messages to maintain the correct order
                    const reversedMessages = data.data.reverse()

                    this.messages = loadMore
                        ? reversedMessages.concat(this.messages)
                        : reversedMessages

                    // Check if there are more messages to load
                    this.hasMoreMessages = data.meta?.current_page < data.meta?.last_page;

                    this.loading = false;
                    this.$nextTick(() => {

                        // Restore the scroll position if it was saved
                        if (scrollPosition) {
                            this.$nextTick(() => this.restoreScrollPosition(scrollPosition))
                        }

                        // Scroll to the bottom only on initial load
                        if (!loadMore) {
                            this.scrollBottom()
                        }
                        this.countDownMessageNotificationInNavigation();
                        this.$event_bus.$emit('remove-messages-from-message-dropdown', this.client.id);
                    });

                })
                .catch(({ error, errorBag }) => {
                    this.loading = false;
                    this.$setErrorBag(errorBag);
                });
        },

        countDownMessageNotificationInNavigation() {
            let read_messages = 0;

            this.messages.forEach((message) => {
                if (!message.is_readed && !message.is_from_user) {
                    read_messages++;
                }
            });

            if (read_messages) {
                this.$event_bus.$emit('remove-from-envelope-message-count', read_messages);
            }
        },

        deleteMessageSwal(message) {
            swal({
                title: trans('online_booking.delete_secure_message'),
                text: trans('online_booking.confirm_delete_secure_message_text'),
                type: "warning",
                showCancelButton: true,
                cancelButtonText: trans('global.cancel'),
                showLoaderOnConfirm: true,
                closeOnConfirm: false,
            }, (confirmed) => {
                if (confirmed) {
                    this.deleteMessage(message);
                }
            });
        },

        deleteMessage(message) {
            http.delete(`/app/client/${this.client.id}/messaging/${message.id}`)
                .then(({ data }) => {
                    // Remove the message from the messages array
                    const deletedMessage = this.messages.find(m => m.id === message.id);

                    if (deletedMessage) {
                        deletedMessage.is_trashed = true
                    }
                    toastr.success(data.success);
                    swal.close();
                })
                .catch(({ error, errorBag }) => {
                    if (error.response.status === 422 && errorBag) {
                        swal.close();
                        this.$setErrorBag(errorBag);
                    }
                });
        },

        deleteAttachmentSwal(message, file) {
            swal({
                title: trans('online_booking.delete_attachment'),
                text: trans('online_booking.confirm_delete_message_attachment_text'),
                type: "warning",
                showCancelButton: true,
                cancelButtonText: trans('global.cancel'),
                showLoaderOnConfirm: true,
                closeOnConfirm: false,
            }, (confirmed) => {
                if (confirmed) {
                    this.deleteAttachment(message, file);
                }
            });
        },

        deleteAttachment(message, file) {
            http.delete(`/app/client/messaging/${message.id}/attachment/${(file ? file.uuid : '')}`)
                .then(({ data }) => {
                    if (file) {
                        this.resetAttachment(message.id, file.uuid);
                    }
                    toastr.success(data.success);
                    swal.close();
                })
                .catch(({ error, errorBag }) => {
                    if (error.response.status === 422 && errorBag) {
                        swal.close();
                        this.$setErrorBag(errorBag);
                    }
                });
        },

	    viewMessageAttachment(file) {
		    this.$event_bus.$emit('show-pdf-preview-modal', {
			    name: file.display_name,
			    url: file.original_url,
		    });
	    },

        markMessageAsUnread(message) {
            http.put(`/app/client/messaging/${message.id}/mark-as-unread`)
                .then(({ data }) => {
                    message.is_readed = true;
                    message.is_marked_as_unread = true;
                    this.$event_bus.$emit('message-marked-as-unread', message.client.id);
                    toastr.success(data.success);
                })
                .catch(({ error, errorBag }) => {
                    this.$setErrorBag(errorBag);
                });
        },

        getInvoiceUrl(invoice) {
            if (invoice.is_created_in_economic || invoice.is_created_in_dinero || invoice.is_created_in_billy || invoice.is_created_in_fiken) {
                return "/app/invoice/"+invoice.id+"/download";
            }

            return invoice.public_url;
        },

        getCreditnoteUrl(creditnote) {
            if (creditnote.is_created_in_economic || creditnote.is_created_in_dinero || creditnote.is_created_in_billy || creditnote.is_created_in_fiken) {
                return `/app/creditnote/${creditnote.id}/download`;
            }

            return creditnote.public_url;
        },

        scrollBottom() {
            const container = document.querySelector('.messages-container');
            if (container) {
                container.scrollTop = container.scrollHeight;
            }
        },

        loadSecureMessagesListener(clientId, clearMessages) {
            if(clientId && clientId !== this.client.id) {
                return;
            }

            // Don't load if this component is disabled
            if (this.disabled) {
                return;
            }

            this.resetPagination();

            if (clearMessages) this.messages = [];
            this.getMessages();
        },

    },

    computed: {
        placeholder_text() {
            var messaging_text = trans('apps.messaging_welcome_encrypted') + " <br/> <br/>" ;

            if (this.client.messaging_sms && this.client.messaging_email) {
                messaging_text += trans('apps.messaging_welcome_notify_email_sms');
            } else if (this.client.messaging_sms && !this.client.messaging_email) {
                messaging_text += trans('apps.messaging_welcome_notify_sms');
            } else if (!this.client.messaging_sms && this.client.messaging_email) {
                messaging_text += trans('apps.messaging_welcome_notify_email');
            } else {
                messaging_text += "<span class='text-danger'>" + trans('apps.messaging_welcome_no_notify') + "</span>";
            }

            return messaging_text;
        },

        can_edit_client() {
            return this.$can('edit-client', this.client);
        },

        can_see_invoices() {
            return this.$canEither('see-invoices', 'see-invoicesassigned');
        },
    },
}
</script>
